import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CalendarClock, CheckCircle2, XCircle, Plus, CalendarX } from "lucide-react";

const EMPTY_IMG = "https://images.unsplash.com/photo-1587522384446-64daf3e2689a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvcmdhbml6ZWQlMjBkZXNrJTIwd29ya3NwYWNlJTIwbWluaW1hbGlzdHxlbnwwfHx8fDE3ODM0NzAwMzF8MA&ixlib=rb-4.1.0&q=85";

function isUpcoming(b) {
  return b.status === "confirmed" && new Date(`${b.date}T${b.time}`) >= new Date(new Date().setHours(0, 0, 0, 0));
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bookings, setBookings] = useState(null);

  const load = () => api.get("/bookings/me").then(({ data }) => setBookings(data)).catch(() => setBookings([]));
  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    try {
      await api.post(`/bookings/${id}/cancel`);
      toast.success("Session cancelled.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const upcoming = (bookings || []).filter(isUpcoming);
  const past = (bookings || []).filter((b) => !isUpcoming(b));

  return (
    <div className="bg-cream min-h-screen" data-testid="dashboard-page">
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="overline text-clay mb-3">Your space</p>
            <h1 className="font-serif font-light text-4xl md:text-6xl text-forest">
              Hello, {user?.name?.split(" ")[0] || "friend"}
            </h1>
          </div>
          <Button onClick={() => navigate("/book")} className="rounded-none bg-forest hover:bg-forest-dark text-cream px-8 py-6 gap-2 transition-colors duration-300" data-testid="dashboard-book-btn">
            <Plus className="w-4 h-4" /> Book a session
          </Button>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          <Metric icon={<CalendarClock className="w-5 h-5" />} label="Upcoming" value={upcoming.length} highlight />
          <Metric icon={<CheckCircle2 className="w-5 h-5" />} label="Completed" value={past.filter((b) => b.status === "confirmed").length} />
          <Metric icon={<XCircle className="w-5 h-5" />} label="Cancelled" value={(bookings || []).filter((b) => b.status === "cancelled").length} />
          <Metric icon={<CalendarClock className="w-5 h-5" />} label="Total" value={(bookings || []).length} />
        </div>

        {/* Upcoming */}
        <p className="overline text-forest mb-6">Upcoming sessions</p>
        {bookings === null ? (
          <div className="h-40 bg-surfacealt animate-pulse" />
        ) : upcoming.length === 0 ? (
          <div className="border border-border bg-white p-10 flex flex-col md:flex-row items-center gap-8" data-testid="empty-upcoming">
            <img src={EMPTY_IMG} alt="Empty" className="w-40 h-40 object-cover" />
            <div>
              <h3 className="font-serif text-2xl text-forest mb-2">No sessions on the horizon</h3>
              <p className="text-muted-foreground mb-4">When you book a session it will appear here, ready for you.</p>
              <Button onClick={() => navigate("/book")} variant="outline" className="rounded-none border-forest text-forest" data-testid="empty-book-btn">Book your first session</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-px bg-border mb-16">
            {upcoming.map((b, i) => (
              <motion.div key={b.booking_id}
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                className="bg-white p-6 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid={`booking-${b.booking_id}`}>
                <div className="flex items-center gap-6">
                  <div className="text-center border-r border-border pr-6">
                    <p className="font-serif text-3xl text-forest leading-none">{b.date.slice(8)}</p>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mt-1">{new Date(b.date).toLocaleDateString("en", { month: "short" })}</p>
                  </div>
                  <div>
                    <h3 className="font-serif text-2xl text-forest">{b.service_name}</h3>
                    <p className="text-sm text-muted-foreground">{b.time} · {b.duration} min</p>
                  </div>
                </div>
                <Button onClick={() => cancel(b.booking_id)} variant="ghost" className="text-destructive hover:bg-destructive/10 gap-2 self-start md:self-center" data-testid={`cancel-${b.booking_id}`}>
                  <CalendarX className="w-4 h-4" /> Cancel
                </Button>
              </motion.div>
            ))}
          </div>
        )}

        {/* History */}
        {past.length > 0 && (
          <>
            <p className="overline text-forest mb-6">History</p>
            <div className="space-y-px bg-border">
              {past.map((b) => (
                <div key={b.booking_id} className="bg-white p-5 flex items-center justify-between opacity-70" data-testid={`history-${b.booking_id}`}>
                  <div>
                    <p className="font-serif text-lg text-forest">{b.service_name}</p>
                    <p className="text-sm text-muted-foreground">{b.date} · {b.time}</p>
                  </div>
                  <span className={`text-xs uppercase tracking-wide px-3 py-1 ${b.status === "cancelled" ? "text-destructive bg-destructive/10" : "text-forest bg-surfacealt"}`}>
                    {b.status}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Metric({ icon, label, value, highlight }) {
  return (
    <div className={`border p-6 ${highlight ? "bg-forest border-forest text-cream" : "bg-white border-border text-forest"}`} data-testid={`metric-${label.toLowerCase()}`}>
      <div className={`mb-4 ${highlight ? "text-clay" : "text-clay"}`}>{icon}</div>
      <p className="font-serif text-4xl leading-none">{value}</p>
      <p className={`text-sm mt-2 ${highlight ? "text-cream/60" : "text-muted-foreground"}`}>{label}</p>
    </div>
  );
}
